import { CSSProperties } from 'react';
import React from 'react';
import { FormField, CheckboxVariant } from '../types';

/**
 * Shared style helpers for the form block — used by both the in-editor
 * BlockRenderer preview and the live PublicTool page so styling stays
 * consistent. All inputs support an optional per-field override layered
 * on top of block-level defaults (props `p`).
 */

const pick = <T>(...vals: (T | undefined | null)[]): T | undefined => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return undefined;
};

/**
 * The placeholder a field actually has.
 *
 * A placeholder of " " looks empty in the editor but is truthy, so it beats
 * every `field.placeholder || fallback` in the renderers: the select's
 * "בחר..." hint disappears and a checkbox whose caption comes from the
 * placeholder renders with no text beside the box. Whitespace-only is
 * therefore treated as "no placeholder" everywhere.
 */
export function fieldPlaceholder(field: { placeholder?: string } | null | undefined): string {
  return (field?.placeholder || '').trim();
}

export function getLabelStyle(p: any, field: FormField): CSSProperties {
  return {
    display: 'block',
    fontSize: pick(field.labelFontSize, p.labelFontSize, 13),
    color: pick(field.labelColor, p.labelColor, '#374151'),
    fontWeight: pick(field.labelFontWeight, p.labelFontWeight, 500) as any,
    marginBottom: pick(p.labelSpacing, 4),
  };
}

export function getInputStyle(p: any, field: FormField): CSSProperties {
  const padV = pick(field.padV, p.inputPadV, 8);
  const padH = pick(field.padH, p.inputPadH, 12);
  const borderW = pick(field.borderWidth, p.inputBorderWidth, 1);
  const fontSize = pick(field.fontSize, p.inputFontSize, 14)!;
  const shadow = p.inputShadow
    ? p.inputShadow === 'lg' ? '0 4px 12px rgba(0,0,0,0.08)'
      : p.inputShadow === 'md' ? '0 2px 6px rgba(0,0,0,0.08)'
      : '0 1px 2px rgba(0,0,0,0.06)'
    : undefined;
  return {
    width: '100%',
    // iOS Safari gives native date inputs a large intrinsic min-width. In a
    // flex form this can make the date field overflow its authored column.
    minWidth: 0,
    maxWidth: '100%',
    display: 'block',
    boxSizing: 'border-box',
    border: `${borderW}px solid ${pick(field.borderColor, p.fieldBorderColor, '#d1d5db')}`,
    borderRadius: pick(field.borderRadius, p.fieldBorderRadius, 6),
    padding: `${padV}px ${padH}px`,
    fontSize,
    lineHeight: 1.5,
    // Native date controls are taller on iOS unless height is explicit.
    height: `calc(${fontSize}px * 1.5 + ${2 * padV! + 2 * borderW!}px)`,
    color: pick(field.textColor, p.inputTextColor, undefined),
    backgroundColor: pick(field.bgColor, p.fieldBgColor, '#ffffff'),
    boxShadow: shadow,
    fontFamily: 'inherit',
  };
}

export function getTextareaStyle(p: any, field: FormField): CSSProperties {
  return { ...getInputStyle(p, field), height: 'auto', resize: 'none' };
}

export function getCheckboxInputStyle(p: any, field: FormField): CSSProperties {
  const size = pick(field.checkboxSize, p.checkboxSize, 16)!;
  const accent = pick(field.checkboxColor, p.checkboxColor, p.submitColor, '#2563eb');
  return {
    width: size, height: size,
    accentColor: accent,
    cursor: 'pointer',
  };
}

export function getCheckboxLabelStyle(p: any, field: FormField): CSSProperties {
  return {
    fontSize: pick(field.fontSize, p.inputFontSize, 14),
    color: pick(field.textColor, p.checkboxLabelColor, '#374151'),
  };
}

export function getFieldWrapStyle(field: FormField): CSSProperties {
  const widthMap = { 'full': '100%', '50%': '50%', '33%': '33%', '66%': '66%' };
  return {
    flex: field.width && field.width !== 'full' ? `0 0 calc(${widthMap[field.width]} - 6px)` : undefined,
    width: !field.width || field.width === 'full' ? '100%' : widthMap[field.width],
    // Flex items default to min-width:auto, so iOS Safari lets a native date
    // control enlarge its wrapper beyond the authored column width.
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    marginTop: field.marginTop,
    marginBottom: field.marginBottom,
    marginLeft: field.marginLeft,
    marginRight: field.marginRight,
  };
}

export function getRequiredMarkStyle(p: any): CSSProperties {
  return { color: p.requiredColor || '#ef4444', marginRight: 2 };
}

/**
 * Compute submit button style from block props.
 * Supports variants, shadow, padding, border, gradient, alignment.
 */
export function getSubmitStyle(p: any, isCampaign?: boolean): CSSProperties {
  const size = p.submitSize || 'md';
  const fallbackPadding = size === 'lg' ? '14px 32px' : size === 'sm' ? '6px 16px' : '10px 24px';
  const fallbackFont = size === 'lg' ? 17 : size === 'sm' ? 13 : 15;
  const padV = p.submitPadV;
  const padH = p.submitPadH;
  const padding = (padV !== undefined && padH !== undefined) ? `${padV}px ${padH}px` : fallbackPadding;

  const shadow = p.submitShadow === 'lg' ? '0 10px 25px rgba(0,0,0,0.2)'
    : p.submitShadow === 'md' ? '0 4px 12px rgba(0,0,0,0.15)'
    : p.submitShadow === 'sm' ? '0 1px 3px rgba(0,0,0,0.12)'
    : undefined;

  const background = p.submitGradient
    ? p.submitGradient
    : (p.submitColor || '#2563eb');

  const borderW = p.submitBorderWidth || 0;
  const border = borderW > 0 ? `${borderW}px solid ${p.submitBorderColor || '#111'}` : 'none';

  return {
    background,
    color: p.submitTextColor || '#fff',
    border,
    borderRadius: p.submitRadius ?? 8,
    padding,
    fontWeight: p.submitFontWeight || 'bold',
    fontSize: p.submitFontSize || fallbackFont,
    width: p.submitFullWidth ? '100%' : 'auto',
    cursor: 'pointer',
    boxShadow: shadow,
    transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
    letterSpacing: p.submitLetterSpacing || undefined,
  };
}

/**
 * Render a single checkbox/radio control with one of 5 visual variants.
 * Used both in disabled-preview (editor) and interactive (public) mode.
 */
export interface CheckboxOptionProps {
  checked: boolean;
  onToggle?: () => void;
  label: string;
  variant?: CheckboxVariant;
  color?: string;
  size?: number;
  textColor?: string;
  textSize?: number;
  disabled?: boolean;
  isRadio?: boolean;
  /** Render a red required-asterisk after the label. Pass when the field's
   *  own label row is hidden (hideLabel) — otherwise the star lives there. */
  required?: boolean;
}

export const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  checked, onToggle, label, variant = 'square',
  color = '#2563eb', size = 18, textColor = '#374151', textSize = 14,
  disabled, isRadio, required,
}) => {
  const handle = disabled ? undefined : onToggle;
  const cursor = disabled ? 'default' : 'pointer';
  const star = required
    ? React.createElement('span', { style: { color: '#ef4444', fontWeight: 700 } }, ' *')
    : null;
  const labelEl = React.createElement('span', { style: { fontSize: textSize, color: textColor } }, label, star);

  // ─ card — full-width tile with checkmark badge ─
  if (variant === 'card') {
    return React.createElement('label', {
      onClick: handle,
      style: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        border: `2px solid ${checked ? color : '#e5e7eb'}`,
        borderRadius: 10,
        background: checked ? `${color}0f` : '#fff',
        cursor,
        transition: 'all 0.15s',
      }
    },
      React.createElement('div', {
        style: {
          width: size, height: size, borderRadius: isRadio ? '50%' : 6,
          background: checked ? color : '#fff',
          border: `2px solid ${checked ? color : '#d1d5db'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: size * 0.7, fontWeight: 'bold',
          flexShrink: 0,
        }
      }, checked ? '✓' : ''),
      labelEl,
    );
  }

  // ─ pill — rounded selectable chip ─
  if (variant === 'pill') {
    return React.createElement('label', {
      onClick: handle,
      style: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 14px',
        border: `1px solid ${checked ? color : '#d1d5db'}`,
        borderRadius: 999,
        background: checked ? color : '#fff',
        color: checked ? '#fff' : textColor,
        fontSize: textSize, fontWeight: checked ? 600 : 500,
        cursor,
        transition: 'all 0.15s',
        userSelect: 'none',
      }
    },
      checked ? React.createElement('span', { style: { fontSize: textSize * 0.9 } }, '✓') : null,
      label,
      star,
    );
  }

  // ─ switch — iOS-style toggle ─
  if (variant === 'switch') {
    const w = Math.round(size * 1.9);
    const h = size;
    const knob = h - 4;
    return React.createElement('label', {
      onClick: handle,
      style: { display: 'flex', alignItems: 'center', gap: 8, cursor }
    },
      React.createElement('div', {
        style: {
          position: 'relative', width: w, height: h, borderRadius: 999,
          background: checked ? color : '#cbd5e1',
          transition: 'background 0.2s',
          flexShrink: 0,
        }
      },
        React.createElement('div', {
          style: {
            position: 'absolute', top: 2, left: checked ? 2 : (w - knob - 2),
            width: knob, height: knob, borderRadius: '50%', background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: 'left 0.2s',
          }
        })
      ),
      labelEl,
    );
  }

  // ─ rounded — circular checkbox ─
  if (variant === 'rounded') {
    return React.createElement('label', {
      onClick: handle,
      style: { display: 'flex', alignItems: 'center', gap: 8, cursor }
    },
      React.createElement('div', {
        style: {
          width: size, height: size, borderRadius: '50%',
          background: checked ? color : '#fff',
          border: `2px solid ${checked ? color : '#d1d5db'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: size * 0.65, fontWeight: 'bold',
          flexShrink: 0,
          transition: 'all 0.15s',
        }
      }, checked ? (isRadio ? React.createElement('div', { style: { width: size * 0.45, height: size * 0.45, borderRadius: '50%', background: '#fff' } }) : '✓') : ''),
      labelEl,
    );
  }

  // ─ square (default) — custom-drawn box, native input kept for a11y ─
  // Was a native <input> sized via width/height. iOS Safari ignores those on
  // native checkboxes and draws its ~13px control, further shrunk by the
  // public page's zoom — signers on a phone saw "a dot" next to paragraph-long
  // consent labels (צוות 7, Sept 2026). Drawing the box ourselves renders the
  // configured size in every engine; the real input stays in the tree
  // (visually hidden) so keyboard/screen-reader users keep a checkbox.
  // Clicking the label TEXT forwards a second click to the inner input, which
  // bubbles back through this handler — preventDefault kills the forwarding
  // (otherwise the toggle fires twice = net no-op). A click ON the input
  // itself keeps its default so the browser doesn't revert the check.
  const box = Math.max(size, 20);
  return React.createElement('label', {
    onClick: handle ? (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault();
      handle();
    } : undefined,
    style: { display: 'flex', alignItems: 'center', gap: 8, cursor, position: 'relative' }
  },
    React.createElement('input', {
      type: isRadio ? 'radio' : 'checkbox',
      checked, readOnly: !onToggle, disabled,
      onChange: () => { /* handled by label onClick */ },
      style: {
        position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0,
        pointerEvents: 'none',
      },
    }),
    React.createElement('div', {
      'aria-hidden': true,
      style: {
        width: box, height: box, minWidth: box, flexShrink: 0,
        boxSizing: 'border-box',
        borderRadius: isRadio ? '50%' : 5,
        background: checked ? color : '#fff',
        border: `2px solid ${checked ? color : '#9ca3af'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: Math.round(box * 0.7), fontWeight: 'bold', lineHeight: 1,
        transition: 'all 0.15s',
      }
    }, checked
      ? (isRadio
        ? React.createElement('div', { style: { width: box * 0.45, height: box * 0.45, borderRadius: '50%', background: '#fff' } })
        : '✓')
      : ''),
    labelEl,
  );
};

export function getSubmitWrapStyle(p: any): CSSProperties {
  const align = p.submitAlign || 'stretch';
  if (p.submitFullWidth) return { width: '100%' };
  return {
    display: 'flex',
    justifyContent: align === 'right' ? 'flex-end' : align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'stretch',
  };
}
